import React, { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// English dictionary. Any key missing falls back to the Serbian default passed to t(key, sr).
// Serbian is the source of truth; we only store English overrides here.
const EN = {
  // order tracking
  "order.title": "Order",
  "order.tell_number": "Tell the staff this number when picking up.",
  "order.waiting_title": "Waiting for restaurant confirmation",
  "order.waiting_body": "Please stay on this page until the restaurant accepts your order. Thank you.",
  "order.waiting_secondary": "The restaurant's response will appear automatically.",
  "order.ready_banner": "YOUR ORDER IS READY",
  "order.rejected": "Order rejected",
  "order.cancelled": "Order cancelled",
  "order.reason_timeout": "The time to confirm the new pickup time expired (60s).",
  "order.reason_customer": "You cancelled the order.",
  "order.reason_restaurant": "The restaurant could not accept the order.",
  "order.newtime_title": "New pickup time",
  "qr.aria": "Order like last time",
  "hours.title": "Opening hours",
  "info.title": "Restaurant information",
  "info.phone": "Phone",
  "info.address": "Address",
  "common.close": "Close",
  "hours.closes_at": "Closes at {time}",
  "hours.opens_at": "Opens at {time}",
  "hours.opens_tomorrow": "Opens tomorrow at {time}",
  "hours.opens_on_day": "Opens {day} at {time}",
  "qr.nothing": "No items from your previous order are available anymore.",
  "qr.some_unavailable": "Some items from your previous order are no longer available.",
  "qr.option_unavailable": "Some options are no longer available. Please review your cart and choose new ones.",
  "qr.replace_same": "You already have items in your cart. Do you want to replace them with your previous order?",
  "qr.replace_other": "Your cart contains items from another restaurant. Do you want to replace it with your previous order?",
  "qr.replace_confirm": "Replace cart",
  "checkout.err_pickup": "Choose a pickup time.",
  "checkout.err_payment": "Choose a payment method.",
  "checkout.not_selected": "Not selected",
  "checkout.pay_unavailable": "The previously selected payment method is no longer available. Choose a new payment method.",
  "order.original_time": "Original time",
  "cd.remaining": "TIME LEFT",
  "cd.late": "LATE",
  "cd.pickup_time": "Pickup time",
  "cd.ready": "YOUR ORDER IS READY FOR PICKUP",
  "cd.reached": "Pickup time reached",
  "cd.waiting_ready": "Waiting for the restaurant to mark the order ready",
  "cd.compact_ready": "Ready",
  "cd.compact_late": "{n} min late",
  "cd.compact_lt1": "<1 min",
  "order.seconds_left": "s to respond",
  "order.accept": "Accept",
  "order.activity": "Activity",
  "order.pickup_time_label": "Pickup time:",
  "order.total": "Total",
  "order.pay_cash": "cash",
  "order.pay_card": "card",
  "order.cancel_order": "Cancel order",
  "order.reorder": "Order again",
  "order.picked_up": "Picked up",
  "order.pickup_confirm_title": "Have you picked up your order?",
  "order.confirm_no": "No",
  "order.confirm_yes": "Yes, picked up",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "item.for_label": "For?",
  "item.for_hint": "Who is this item for? (optional)",
  "item.for_ph": "e.g. Mark",
  "item.note_label": "Note",
  "item.note_ph": "e.g. no onions",
  "item.for_prefix": "For",
  "item.note_prefix": "Note",
  // statuses (progress + busy)
  "status.busy_title": "The restaurant is currently busy.",
  "status.busy_body": "Earliest available pickup is in 30 minutes.",
  // nav / common
  "nav.order_number": "Order number",
  "status.new": "Sent",
  "status.accepted": "Accepted",
  "status.preparing": "Preparing",
  "status.ready": "Ready",
  "status.completed": "Picked up",
  "toast.accepted": "Order accepted!",
  "toast.preparing": "Your order is being prepared",
  "toast.ready": "Your order is ready!",
  "toast.completed": "Enjoy! Order picked up.",
  "toast.cancelled": "Order was cancelled",
  "toast.rejected": "Order was rejected",
  "toast.time_change": "New pickup time proposed",
  "order.confirm_cancel": "Cancel this order?",
  "order.cancelled_toast": "Order cancelled",
  "order.reorder_added": "Added to cart at current prices",
  "common.error": "Something went wrong",
  // product modal / menu
  "menu.required": "Required",
  "menu.unavailable": "Unavailable",
  "menu.extras": "Extras",
  "menu.note_placeholder": "Note (e.g. no onions)",
  "menu.add": "Add",
  "menu.currently_unavailable": "Currently unavailable",
  "menu.added_to_cart": "Added to cart",
  "menu.choose": "Please choose:",
  "menu.cannot_order_options": "This item can't be ordered right now due to unavailable options",
  // homepage
  "home.tagline": "Order. Pick up. Enjoy.",
  "home.search": "Search restaurants",
  "home.nearby_title": "Closest to you",
  "home.loc_denied": "Location is off. You can search restaurants manually.",
  "home.choose_city": "Choose a city",
  "home.all_cities": "All cities",
  "home.retry": "Try again",
  "home.loc_prompt": "Allow location access to find restaurants near you.",
  "home.locating": "Finding your location...",
  "home.show_nearby": "Closest to you",
  "home.your_location": "Your location",
  "home.change_location": "Change location",
  "home.no_nearby": "No restaurants with a known location nearby.",
  "home.restaurants": "Restaurants",
  "home.nearest": "Nearest",
  "home.none_available": "No restaurants available right now.",
  "home.view_menu": "View menu",
  "home.pickup_from": "Pickup from",
  "home.min": "min",
  "home.active_order": "Active order",
  "home.verify_email_banner": "Verify your email to place orders",
  "badge.closed": "Closed",
  "badge.open": "Open",
  "badge.paused": "Paused",
  "acct.phone": "Phone number",
  "acct.phone_hint": "The restaurant will contact you on this number for new orders.",
  "acct.save": "Save",
  "acct.phone_saved": "Phone number saved",
  "acct.phone_error": "Failed to save number",
  "acct.my_orders": "My orders",
  "acct.active_orders": "Active orders",
  "acct.order_history": "Order history",
  "order.order_date": "Order date:",
  "acct.restaurant_panel": "Restaurant panel",
  "acct.admin_panel": "Admin panel",
  "acct.logout": "Log out",
  "acct.open_profile": "Open profile",
  "acct.language": "Language",
  "acct.lang_sr": "Serbian",
  "acct.lang_en": "English",
  "acct.account_section": "Account",
  "acct.settings_section": "Settings",
  "acct.access_section": "Access",
  "acct.city": "City",
  "city.choose_title": "Choose city",
  "badge.happy_hour": "Happy Hour",
  "badge.hiring": "We're hiring",
  "city.allow_title": "Allow location access",
  "city.allow_body": "Ponesi uses your location to automatically determine your current city.",
  "city.allow_btn": "Allow location",
  "city.detecting": "Detecting your city…",
  "city.failed": "We couldn't automatically determine your city.",
  "city.unsupported": "Ponesi is not available in your city yet.",
  "city.choose_manual": "Please choose a city manually.",
  "city.manual_link": "Choose city manually",
  "notify.title": "Order notifications",
  "notify.on": "Enabled",
  "notify.off": "Disabled",
  "notify.enable_pill": "Turn on notifications",
  "notify.enabled_toast": "Notifications enabled",
  "notify.disabled_toast": "Notifications disabled",
  "notify.blocked_toast": "Notifications are blocked in browser settings",
  "notify.enable_failed": "Could not enable notifications",
  "notify.ios_install": "Turn on notifications by installing Ponesi to your home screen.",
  "notify.ios_how": "How to install?",
  "notify.ios_step_share": "Tap Share in Safari",
  "notify.ios_step_add": "Choose \"Add to Home Screen\"",
  "notify.ios_step_open": "Open Ponesi from the home screen",
  "notify.ios_step_enable": "Tap \"Turn on notifications\"",
  "acct.logout_toast": "You have been logged out",
  "acct.no_orders": "You have no orders yet.",
  "acct.items": "items",
  "acct.contact": "Contact",
  "acct.privacy": "Privacy Policy",
  "acct.terms": "Terms of Use",
  // Order-level customer note
  "cart.note_label": "Note for the restaurant",
  "cart.note_placeholder": "E.g. Pack the order in two separate bags.",
  "cart.note_optional": "Optional",
  "checkout.note_summary": "Note for the restaurant",
  // PWA install
  "pwa.title": "Keep Ponesi within reach",
  "pwa.desc": "Add Ponesi to your Home Screen for faster ordering.",
  "pwa.install": "Install Ponesi",
  "pwa.dismiss": "Not now",
  "pwa.got_it": "OK",
  "pwa.ios_title": "Add Ponesi to your Home Screen",
  "pwa.ios_1a": "Tap",
  "pwa.ios_1b": "in Safari",
  "pwa.ios_2a": "Choose",
  "pwa.ios_2b": "Add to Home Screen",
  "pwa.ios_3": "Confirm “Add”",
  "pwa.inapp_desc": "Open Ponesi in Safari to add it to your Home Screen.",
  "acct.install": "Install Ponesi",
  // restaurant page
  "rp.not_found": "Restaurant not found",
  "rp.back_ponesi": "Back to Ponesi",
  "rp.menu": "Menu",
  "rp.cart": "Cart",
  "rp.pickup_from": "Pickup from ~",
  "rp.min": "min",
  "rp.unavailable": "Unavailable",
  "rp.closed_now": "WE ARE CURRENTLY CLOSED",
  "rp.not_accepting": "NOT ACCEPTING ORDERS RIGHT NOW",
  "rp.conflict_body_1": "Your cart currently contains items from",
  "rp.conflict_body_2": "Do you want to empty the cart and continue with another restaurant?",
  "rp.conflict_cancel": "Cancel",
  "rp.conflict_clear": "Empty cart",
  "rp.closed_block": "Restaurant is closed",
  "rp.not_accepting_block": "Not accepting orders",
  // cart
  "cart.title": "Your cart",
  "cart.empty": "Your cart is empty",
  "cart.total": "Total",
  "cart.checkout": "Continue to checkout",
  // checkout
  "checkout.title": "Checkout",
  "checkout.login_prompt": "Sign in to place an order",
  "checkout.google": "Sign in with Google",
  "checkout.phone": "Phone number",
  "checkout.phone_hint": "Needed for order notifications.",
  "checkout.enter_phone": "Enter a phone number",
  "checkout.save_phone_err": "Error saving the number",
  "checkout.pickup_time": "Pickup time",
  "checkout.busy_notice": "The restaurant is currently busy. Earliest pickup is in 30 minutes.",
  "checkout.asap": "ASAP",
  "checkout.asap_blocked": "For orders over 3,000 RSD, please select a pickup time.",
  "checkout.asap_blocked_hint": "Larger orders require additional preparation time.",
  "checkout.schedule": "Schedule",
  "checkout.no_slots": "No available slots.",
  "checkout.payment": "Payment method",
  "checkout.cash": "Cash at pickup",
  "checkout.card": "Card at restaurant",
  "checkout.total": "Total",
  "checkout.place": "Place order",
  "checkout.sending": "Sending…",
  "checkout.confirm_place": "Confirm & order",
  "checkout.cant_order": "Ordering unavailable",
  "checkout.choose_time": "Choose a pickup time",
  "checkout.placed": "Order sent!",
  "checkout.product_removed": "An item is no longer available and was removed from your cart. Please review and try again.",
  "checkout.generic_err": "Your order couldn't be sent right now. Please try again.",
  // order tracking
  "order.items_skipped_suffix": "item(s) unavailable and skipped",
  "tl.title": "Activity",
  "tl.empty": "No recorded events.",
  "tl.ORDER_CREATED": "Order placed",
  "tl.ORDER_ACCEPTED": "Accepted",
  "tl.ORDER_PREPARING": "Preparation started",
  "tl.ORDER_READY": "Ready for pickup",
  "tl.ORDER_COMPLETED": "Picked up",
  "tl.ORDER_REJECTED": "Rejected",
  "tl.PICKUP_TIME_PROPOSED": "New time proposed",
  "tl.PICKUP_TIME_ACCEPTED": "Customer accepted new time",
  "tl.PICKUP_TIME_DECLINED": "Customer declined new time",
  "tl.PICKUP_TIME_EXPIRED": "Auto-cancelled — deadline expired (60s)",
  "tl.CUSTOMER_CANCELLED": "Customer cancelled",
  "tl.RESTAURANT_CANCELLED": "Restaurant cancelled",
  "tl.SYSTEM_CANCELLED": "System cancellation",
  "notify.blocked": "Notifications are blocked. Enable them in your browser settings.",
  "notify.unsupported": "Notifications aren't supported on this device.",
  "checkout.product_issue": "An item is currently unavailable. Please review your cart and try again.",
  "ev.title": "Email verification",
  "ev.sent_to": "We sent a code to",
  "ev.enter": "Enter it so you can place your order.",
  "ev.code_sent": "Code sent to your email",
  "ev.send_err": "Error sending the code",
  "ev.enter_code": "Enter the code from the email",
  "ev.verified": "Email verified",
  "ev.wrong": "Incorrect code. Please try again.",
  "ev.checking": "Checking…",
  "ev.confirm": "Confirm",
  "ev.resend_in": "Resend in",
  "ev.resend": "Resend code",
  "checkout.terms_required": "You must accept the Terms of Use to place an order",
  "checkout.terms_pre": "I accept the ",
  "checkout.terms_link": "Terms of Use",
  "checkout.terms_mid": " and confirm I am aware of the ",
  "checkout.privacy_link": "Privacy Policy",
  "checkout.terms_post": ".",
  // product config
  "menu.optional": "Optional",
  "menu.choose_verb": "Choose",
  "menu.up_to": "Up to",
  "menu.at_least": "At least",
  "menu.your_choice": "Your selection",
  "menu.add_to_cart": "Add to cart",
  // order summary
  "checkout.summary_title": "Order summary",
  "checkout.edit": "Edit",
  "checkout.payment_short": "Payment",
  "checkout.pickup_short": "Pickup time",
  "checkout.asap_label": "ASAP",
  "checkout.payment_changed": "The restaurant no longer supports the selected payment method. Please choose an available one.",
  "home.clear_search": "Clear search",
  "home.no_results": "No results found.",
  "home.outside_area": "Ponesi is not available in your city yet. You can pick another city and browse restaurants.",
  "checkout.back": "Back to cart",
  "cart.back": "Back to menu",
};

const I18nContext = createContext({ locale: "sr", t: (k, sr) => sr, localePath: (p) => p, switchLocale: () => {} });

export function useI18n() {
  return useContext(I18nContext);
}

function isCustomerPath(pathname) {
  const p = pathname.replace(/^\/en(\/|$)/, "/");
  // Staff/admin surfaces are Serbian-only (no switcher).
  if (p.startsWith("/admin") || p.startsWith("/team")) return false;
  if (p === "/restaurant" || p.startsWith("/restaurant/login")) return false;
  // Staff restaurant area: /restaurant/{rid}/{section}. The customer menu is /restaurant/{slug} (single segment).
  if (/^\/restaurant\/[^/]+\/.+/.test(p)) return false;
  return true;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
  const locale = isEn ? "en" : "sr";

  const t = (key, sr) => (locale === "en" ? (EN[key] ?? sr) : sr);

  // Prefix a customer path with the active locale.
  const localePath = (to) => {
    if (!to.startsWith("/")) return to;
    return locale === "en" ? `/en${to === "/" ? "" : to}` : to;
  };

  // Switch language while staying on the semantically-equivalent page (preserves cart/login/order via contexts).
  const switchLocale = (target) => {
    try { localStorage.setItem("ponesi_language", target); } catch {}
    const { pathname, search, hash } = window.location;
    let base = pathname.replace(/^\/en(?=\/|$)/, "") || "/";
    const next = target === "en" ? `/en${base === "/" ? "" : base}` : base;
    navigate(next + search + hash);
  };

  // Persisted explicit language preference wins on future visits. If the customer previously chose
  // English, auto-route Serbian customer URLs to their /en equivalent (never the other way — an
  // explicit "sr" choice or a shared /en link is respected as-is). Only customer surfaces.
  useEffect(() => {
    let pref = null;
    try { pref = localStorage.getItem("ponesi_language"); } catch {}
    if (pref !== "en") return;
    if (!isCustomerPath(location.pathname)) return;
    if (location.pathname === "/en" || location.pathname.startsWith("/en/")) return;
    const base = location.pathname === "/" ? "" : location.pathname;
    navigate(`/en${base}` + location.search + location.hash, { replace: true });
    // eslint-disable-next-line
  }, [location.pathname]);

  // SEO + a11y: html lang, canonical + hreflang alternates for customer pages.
  useEffect(() => {
    document.documentElement.lang = locale;
    const base = "https://ponesi.rs";
    const path = location.pathname.replace(/^\/en(?=\/|$)/, "") || "/";
    const set = (rel, href, hreflang) => {
      const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("link"); el.rel = rel; if (hreflang) el.hreflang = hreflang; document.head.appendChild(el); }
      el.href = href;
    };
    if (isCustomerPath(location.pathname)) {
      set("canonical", base + (locale === "en" ? `/en${path === "/" ? "" : path}` : path));
      set("alternate", base + (path === "/" ? "" : path), "sr");
      set("alternate", base + `/en${path === "/" ? "" : path}`, "en");
    }
  }, [location.pathname, locale]);

  const showSwitcher = isCustomerPath(location.pathname);
  void showSwitcher;

  return (
    <I18nContext.Provider value={{ locale, t, localePath, switchLocale }}>
      {children}
    </I18nContext.Provider>
  );
}
